import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { Navigate, useLocation } from "react-router-dom";
import { apiFetch } from "./api";

export type PublicUser = {
  id: string;
  email: string;
  role: "MEMBER" | "ADMIN";
  status: "PENDING" | "REJECTED" | "APPROVED_UNPAID" | "ACTIVE" | "LAPSED";
  fullName: string;
  preferredName: string | null;
  photoUrl: string | null;
  bio: string | null;
};

type AuthState = {
  user: PublicUser | null;
  loading: boolean;
  refresh: () => Promise<void>;
  login: (email: string, password: string) => Promise<PublicUser>;
  logout: () => Promise<void>;
};

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<PublicUser | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const data = await apiFetch<{ user: PublicUser | null }>("/api/auth/me");
      setUser(data.user);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const login = useCallback(async (email: string, password: string) => {
    const data = await apiFetch<{ user: PublicUser }>("/api/auth/login", {
      method: "POST",
      body: { email, password },
    });
    setUser(data.user);
    return data.user;
  }, []);

  const logout = useCallback(async () => {
    await apiFetch("/api/auth/logout", { method: "POST" });
    setUser(null);
  }, []);

  const value = useMemo<AuthState>(
    () => ({ user, loading, refresh, login, logout }),
    [user, loading, refresh, login, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
}

function LoadingShell() {
  return (
    <div
      className="container section"
      style={{ minHeight: "60vh", textAlign: "center" }}
    >
      <p className="eyebrow">Loading…</p>
    </div>
  );
}

/**
 * Gate a route. Members must be ACTIVE; admins are always allowed.
 * APPROVED_UNPAID / PENDING / LAPSED are redirected to /login with a hint.
 */
export function RequireAuth({ children }: { children: ReactNode }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <LoadingShell />;
  if (!user) {
    return (
      <Navigate
        to={`/login?next=${encodeURIComponent(location.pathname)}`}
        replace
      />
    );
  }
  const isAdmin = user.role === "ADMIN";
  if (!isAdmin && user.status !== "ACTIVE") {
    return <Navigate to={`/login?status=${user.status}`} replace />;
  }
  return <>{children}</>;
}

/** Admin-only route gate. */
export function RequireAdmin({ children }: { children: ReactNode }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <LoadingShell />;
  if (!user) {
    return (
      <Navigate
        to={`/login?next=${encodeURIComponent(location.pathname)}`}
        replace
      />
    );
  }
  if (user.role !== "ADMIN") {
    return <Navigate to="/portal" replace />;
  }
  return <>{children}</>;
}
